import * as Sentry from '@sentry/browser';

import { isProduction } from '../../helpers';

function isEventComingFromSrc(event, hint, src) {
  try {
    const srcLower = src.toLowerCase();

    // Check error message
    if (event.message && event.message.toLowerCase().includes(srcLower)) {
      return true;
    }

    // Check hint error
    if (hint && hint.originalException) {
      const error = hint.originalException;
      if (error && error.message && error.message.toLowerCase().includes(srcLower)) {
        return true;
      }
    }

    // Check exception stack traces
    if (event.exception && Array.isArray(event.exception.values)) {
      for (const exceptionValue of event.exception.values) {
        if (exceptionValue.stacktrace && Array.isArray(exceptionValue.stacktrace.frames)) {
          for (const frame of exceptionValue.stacktrace.frames) {
            if ((frame.filename && frame.filename.toLowerCase().includes(srcLower)) || (frame.function && frame.function.toLowerCase().includes(srcLower))) {
              return true;
            }
          }
        }
      }
    }

    // Check culprit
    if (event.culprit && event.culprit.toLowerCase().includes(srcLower)) {
      return true;
    }
  } catch (e) {
    console.error('[Sentry] Error in _eventComingFromSrc: ', e);
  }

  return false;
}

window.NapkinSentry = {
  errorHashes: {},

  init: function () {
    try {
      if (!isProduction()) {
        console.log('Skipping Sentry on non-production environment.');
        return;
      }

      Sentry.init({
        dsn: 'https://6683135a62807899439009931511fbcd@o4507804332654592.ingest.de.sentry.io/4507889990041680',

        release: window.gitCommit || 'unknown',

        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.browserProfilingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],

        sendDefaultPii: true, // Send the user's IP address

        // Tracing
        tracesSampleRate: 0.1, //  Capture 10% of the transactions

        tracePropagationTargets: ['localhost', /^https:\/\/napkin\.ai\/api/],

        // Session Replay
        replaysSessionSampleRate: 0, // Disable automated session replay
        replaysOnErrorSampleRate: 1.0, // Capture 100% of the errors for manual replay

        profilesSampleRate: 1.0,

        ignoreErrors: [
          // JS errors safe to ignore
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured with value',
          'Error looking up record in object store by key range', // Safari error
          'WKWebView API client did not respond to this postMessage', // Safari error
          'Invalid call to runtime.sendMessage(). Tab not found.',
          'Invalid event type from worker: undefined',
          'Unexpected EOF', // File not loaded completely
          'NS_ERROR_FILE_CORRUPTED', // Firefox error
          'NS_ERROR_NOT_INITIALIZED', // Firefox error
          'NS_ERROR_ABORT', // Firefox error
          'NS_ERROR_FAILURE', // Firefox error
          'NS_ERROR_UNEXPECTED', // Firefox error
          'NS_ERROR_FILE_NOT_DIRECTORY', // Firefox error
          'The operation is insecure.', // Firefox error
          'sendBeacon', // Probably from a browser extension or open replay

          // CSS errors
          `Failed to execute 'insertRule' on 'CSSStyleSheet'`, // Probably coming from Intercom

          // ES6 errors
          'Invalid or unexpected token',
          'TypeError: Illegal invocation',
          'Unexpected token',
          'Unexpected end of script',
          'Unexpected end of input',
          'Promise.resolve is not a function',

          // Network errors
          'Failed to fetch',
          'Load failed',
          'AbortError',
          'NetworkError when attempting to fetch resource.',
          'NS_ERROR_FILE_NOT_FOUND',
          `Unexpected identifier 'https`,

          // IndexedDB errors
          'Connection to Indexed Database server lost. Refresh the page to try again',
          'Database deleted by request of the user',
          'Encountered full disk while opening backing store for indexedDB.open.',
          'Unable to open database file on disk',
          'Internal error opening backing store for indexedDB.open.',
          'Internal error deleting database.',
          'Object store cannot be found in the database',

          // Sentry errors
          'secret is not defined',
          'zaloJSV2',
          'Stylesheet append timeout',
          '(type=unhandledrejection) captured as promise rejection',

          // LaunchDarkly errors
          'LaunchDarklyFlagFetchError',

          // Google Analytics errors
          'GA is not defined',

          // Firebase errors
          '__spreadArray',
          `can't redefine non-configurable property "get"`, // https://second-layer.sentry.io/share/issue/68d1116c19504771a767057ab4a56a22/

          // Facebook browser
          'IFrameMessageHandler', // Coming from Facebook browser on iOS

          // Chrome webview
          'onReady is not defined', // Chrome webview error (https://second-layer.sentry.io/issues/8844506/events/836289c7fecf40ae8bf3763bfee3d56e/)
          'onHide is not defined', // Chrome webview error (https://second-layer.sentry.io/issues/8844500/events/6e9c4147cfca41c4a8766d2a58fbd28d/)

          // Chrome extension errors
          `Cannot read properties of undefined (reading 'checker')`,

          // Safari browser
          'window.webkit.messageHandlers',
          'kCFErrorDomainCFNetwork',
          'Attempting to change value of a readonly property.',
          'Can only call Window.structuredClone on instances of Window',

          // iOS errors
          'The object does not support the operation or argument.', // iOS error

          // Other errors
          'TypeError: window.checkLogin is not a function', // Not sure from where this error is coming
          'Origin is only applicable schemes http and https: file://', // Local file error
          'window.performance.getEntriesByName', // web.dev error
          'jb', // Unknown error
          '_chromeNamespace', // Unknown error
          'contentWindow', // Extension trying to access the content window
          `Can't find variable: post`, // Unknown error
          `Can't find variable: GM_getValue`, // Unknown error
          `Can't find variable: unsafeWindow`, // Unknown error
          `Can't find variable: gmo`, // Unknown error
          `undefined is not an object (evaluating 'a.K')`, // Unknown error
          `undefined is not an object (evaluating 'a.J')`, // Intercom widget error
          `null is not an object (evaluating 'e.contentDocument.body')`, // Intercom widget error
          'webVitals.getCLS is not a function', // Probably from a crawler
          'webVitals is not defined', // Probably from a crawler
          'jQuery is not defined', // We don't use jQuery
          '$ is not defined', // We don't use jQuery
          '$ is not a function', // We don't use jQuery
          'customDispatchEvent is not defined', // elm-loading (https://second-layer.sentry.io/share/issue/58bcc805c2f6450d83fdfde6e2d4f5c4/)
          'Java bridge method invocation error', // Cordova?
          'Java object is gone', // Cordova or React Native?
          'WebViewJavascriptBridge is not defined', // Cordova or React Native?
          'Java exception was raised during method invocation', // Instagram & FB browser error
          'GetHTMLElementsAtPoint', // Webview error
          'Data cannot be cloned', // Webview error
          'window[objectName][methodName] is not a function', // Webview error
          'window[functionName] is not a function', // Webview error
          'WebAssembly', // WebAssembly error (we don't use WebAssembly)
          'UCShellJava.sdkEventFire', // UC Browser error
          'zp_token', // Unknown error
          'repeatInterval_c', // Unknown error
          'shadowDOMUnlocker is not defined', // Unknown error
          'toscaAlertAlerter is not defined', // Unknown error
          '_satellite?.setDebug', // Unknown error
          'Mx is not defined', // Unknown error
          'LIDNotifyId is not defined', // Unknown error
          'nativeInitedNotify is not defined', // Unknown error
          'frappe is not defined', // Unknown error
          'ethereum', // Probably from an extension
          'wsimtgo', // Unknown error
          'HTMLOUT is not defined', // Unknown error
          'yandexReadabilityChecker', // Yandex crawler
          '_logicalFormForControl', // iCloud password manager
          'GM_listValues', // GreaseMonkey error
          'WeixinJSBridge', // WeChat browser error
          'getCosmeticsFilters', // Coming from ghostery/adblocker
          'change_ua', // Unknown error
          /^Cannot read properties of null (reading 'document')$/, // Unknown error
          'Menu caption text is required!', // Unknown error
          'webtranslation_detect', // Failed to load 'https://searchaggr-dre.dt.dbankcloud.com/search/api/v1/webtranslation_detect'.
        ],

        beforeSend(event, hint) {
          try {
            // Limit the number of events sent to Sentry
            if (event.message && window.NapkinSentry.errorHashes[event.message] > 5) {
              return null;
            }

            // Increase the count of the error
            window.NapkinSentry.errorHashes[event.message] = window.NapkinSentry.errorHashes[event.message]
              ? window.NapkinSentry.errorHashes[event.message] + 1
              : 1;
          } catch (e) {
            console.error('[Sentry] Error in beforeSend: ', e);
          }

          // Ignore errors coming from a local file
          if (isEventComingFromSrc(event, hint, 'file://')) {
            console.warn('[Sentry] Ignoring local file error: ', event.message);
            return null;
          }

          // Ignore errors coming from a blob
          if (isEventComingFromSrc(event, hint, 'blob:')) {
            console.warn('[Sentry] Ignoring blob file error: ', event.message);
            return null;
          }

          // Ignore errors coming from the browser extensions
          if (isEventComingFromSrc(event, hint, 'chrome-extension://')) {
            console.warn('[Sentry] Ignoring chrome extension error: ', event.message);
            return null;
          }
          if (isEventComingFromSrc(event, hint, 'moz-extension://')) {
            console.warn('[Sentry] Ignoring firefox extension error: ', event.message);
            return null;
          }

          // Ignore errors coming from clarity.js
          if (isEventComingFromSrc(event, hint, 'clarity.js')) {
            console.warn('[Sentry] Ignoring clarity.js error: ', event.message);
            return null;
          }

          // Ignore errors coming from @sentry-internal
          if (isEventComingFromSrc(event, hint, '@sentry-internal') || isEventComingFromSrc(event, hint, '@sentry')) {
            console.warn('[Sentry] Ignoring sentry-internal error: ', event.message);
            return null;
          }

          // Ignore errors coming from OpenReplay
          if (isEventComingFromSrc(event, hint, '@openreplay')) {
            console.warn('[Sentry] Ignoring openreplay error: ', event.message);
            return null;
          }

          // Ignore errors coming from Google Tag Manager
          if (isEventComingFromSrc(event, hint, 'gtm.js')) {
            console.warn('[Sentry] Ignoring Google Tag Manager error: ', event.message);
            return null;
          }

          // Ignore google translate errors
          if (isEventComingFromSrc(event, hint, 'translate.googleapis.com') || isEventComingFromSrc(event, hint, 'translate_http')) {
            return null;
          }

          // Ignore errors coming from Intercom
          if (isEventComingFromSrc(event, hint, 'intercomcdn.com')) {
            return null;
          }

          // Ignore errors coming from the rocket-loader.min.js (Cloudflare)
          if (isEventComingFromSrc(event, hint, 'rocket-loader.min.js')) {
            return null;
          }

          // Ignore errors coming from the browser UC Browser
          if (isEventComingFromSrc(event, hint, 'u.c.b.r.o.w.s.e.r')) {
            return null;
          }

          return event;
        },

        beforeBreadcrumb(breadcrumb, hint) {
          try {
            // Filter out debug breadcrumbs
            if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
              return null;
            }
          } catch (e) {
            // console.error('[Sentry] Error in beforeBreadcrumb: ', e);
          }
          return breadcrumb;
        },
      });

      console.debug('[Sentry] Loaded');
    } catch (e) {
      console.error('[Sentry] Error in loading Sentry: ', e);
    }

    // Manually start the recording if the URL contains the debug-session=true query parameter
    if (window.location.search.includes('debug-session=true')) {
      try {
        const replay = Sentry.getReplay();
        replay.start();
        // Tag the session with the debug-record tag
        Sentry.setTag('debug-record', 'true');

        console.debug('[Sentry] Session Replay Started from debug-session=true query parameter');

        const emailAddress = window.location.search.match(/email=([^&]*)/);
        if (emailAddress) {
          Sentry.setUser({
            email: emailAddress[1],
          });
          console.debug('[Sentry] User email set for the session replay', emailAddress[1]);
        }
      } catch (e) {
        console.error('[Sentry] Error in starting the session replay: ', e);
      }
    }
  },

  getClient: function () {
    return Sentry;
  },

  _isEventComingFromSrc: function (event, src) {
    try {
      if ((event.message && event.message.includes(src)) || event.exception?.values?.[0]?.stacktrace?.frames?.some((f) => f.filename?.includes(src))) {
        return true;
      }
    } catch (e) {
      console.error('[Sentry] Error in _eventComingFromSrc: ', e);
    }

    return false;
  },
};

function loadSentry() {
  window.NapkinSentry.init();
}

export default loadSentry;
